import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, MenuItem } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { Add as AddIcon, Remove as RemoveIcon } from '@material-ui/icons';
import { map } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import NgpTextField from '../../../../form/NgpTextField';
import NgpSelect from '../../../../form/NgpSelect';
import NgpToggle from '../../../../form/NgpToggle';
import NgpRow from '../../../../form/NgpRow';
import { DEFAULT_INPUT_PROPS } from '../../../../../utilities/FormUtilities';
import {
    EMPTY_POST,
    GET_METHOD,
    METHOD_NAMES,
    POST_METHOD,
} from '../../../../panels/web-test-now-panels/WebTestConstants';
import TestAuthenticationSection from './TestAuthenticationSection';
import { DEFAULT_HEADER, EMPTY_HEADER } from '../../../../panels/web-test-now-panels/WebTestConfig';
import useSyncedFieldArray from '../hooks/UseSyncedFieldArray';
import { RequestHeaderValueField } from '../../../../panels/web-test-now-panels/RequestHeaderBuilderPopover';
import { useNoPaddingLeftStyles } from '../../../../form/FormStyles';
import TestFormSection from './TestFormSection/TestFormSection';
import TestSubFormSection from './TestFormSection/TestSubFormSection';

const useStyles = makeStyles(() => ({
    dynamicRow: {
        display: 'grid',
        gridTemplateColumns: '150px 380px auto',
        columnGap: 24,
        alignItems: 'center',
        marginBottom: 20,
    },
    headerRow: {
        display: 'grid',
        gridTemplateColumns: '150px 380px auto',
        columnGap: 24,
        alignItems: 'center',
        marginBottom: 20,
    },
    actionButton: {
        width: 48,
        height: 48,
        backgroundColor: '#4f6f84',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#3f5e71',
        },
    },
}));

const TestRequestParametersSection = ({ authTypeRef }) => {
    const classes = useStyles();
    const noPaddingLeftStyles = useNoPaddingLeftStyles();
    const { control, watch, setValue, getValues } = useFormContext();
    const method = watch('testConfig.method') || GET_METHOD;
    const showPostData = method === POST_METHOD;

    const {
        fields: postDataFields,
        append: appendPostData,
        remove: removePostData,
    } = useFieldArray({ control, name: 'testConfig.postData' });

    const {
        fields: httpHeadersFields,
        append: appendHttpHeaders,
        remove: removeHttpHeaders,
    } = useFieldArray({ control, name: 'testConfig.httpHeaders' });

    // Initialize httpHeaders with default User-Agent if empty
    useEffect(() => {
        if (httpHeadersFields.length === 0) {
            const currentHeaders = getValues('testConfig.httpHeaders');
            if (Array.isArray(currentHeaders) && currentHeaders.length > 0) {
                appendHttpHeaders(
                    currentHeaders.map((h) => ({
                        name: h?.name || '',
                        value: h?.value || '',
                    }))
                );
            } else {
                appendHttpHeaders({ ...DEFAULT_HEADER });
            }
        }
    }, [httpHeadersFields.length, appendHttpHeaders, getValues]);

    // Sync field array values to Controllers after they mount (see UseSyncedFieldArray for details)
    useSyncedFieldArray('testConfig.httpHeaders', httpHeadersFields, setValue, ['name', 'value']);

    // Auto-append initial empty row when switching to POST method to improve UX:
    // - Follows Progressive Disclosure principle (show initial input immediately)
    // - Provides better empty state feedback than showing only an Add button
    // - Does NOT set isDirty because it aligns with portal/ui behavior
    useEffect(() => {
        if (showPostData && postDataFields.length === 0) {
            const currentPosts = getValues('testConfig.postData');
            if (Array.isArray(currentPosts) && currentPosts.length > 0) {
                // Existing POST data exists — sync it into the field array
                appendPostData(
                    currentPosts.map((p) => ({
                        name: p?.name || '',
                        value: p?.value || '',
                    }))
                );
            } else {
                // No existing POST data — auto-append initial empty row
                // This provides better UX than showing only an Add button
                appendPostData({ ...EMPTY_POST });
            }
        }
    }, [showPostData, postDataFields.length, appendPostData, getValues]);

    // Sync postData field array values to Controllers after they mount
    useSyncedFieldArray('testConfig.postData', postDataFields, setValue, ['name', 'value'], showPostData);

    return (
        <TestFormSection title={<Trans>Request Parameters</Trans>}>
            <NgpRow>
                <NgpSelect fieldName="testConfig.method" label={t`Method`} required>
                    {map(METHOD_NAMES, (label, key) => (
                        <MenuItem key={key} value={key}>
                            {label}
                        </MenuItem>
                    ))}
                </NgpSelect>
                <div style={{ width: 650 }}>
                    <NgpTextField
                        fieldName="testConfig.url"
                        label={t`URL`}
                        required
                        inputProps={DEFAULT_INPUT_PROPS}
                        fullWidth
                    />
                </div>
            </NgpRow>

            {showPostData && (
                <>
                    {postDataFields.map((field, index) => (
                        <Box className={classes.dynamicRow} key={field.id || `post-${index}`}>
                            <NgpTextField
                                fieldName={`testConfig.postData[${index}].name`}
                                label={t`Post Name`}
                                inputProps={DEFAULT_INPUT_PROPS}
                                fullWidth
                            />
                            <NgpTextField
                                fieldName={`testConfig.postData[${index}].value`}
                                label={t`Post Value`}
                                inputProps={DEFAULT_INPUT_PROPS}
                                fullWidth
                            />
                            <IconButton
                                className={classes.actionButton}
                                onClick={
                                    index === 0 ? () => appendPostData({ ...EMPTY_POST }) : () => removePostData(index)
                                }
                            >
                                {index === 0 ? <AddIcon /> : <RemoveIcon />}
                            </IconButton>
                        </Box>
                    ))}
                </>
            )}

            <TestSubFormSection
                fieldsClassName={noPaddingLeftStyles.ngpFormFields}
                title={<Trans>Request Headers</Trans>}
            >
                {httpHeadersFields.map((header, index) => (
                    <Box className={classes.headerRow} key={header.id || `header-${index}`}>
                        <NgpTextField
                            fieldName={`testConfig.httpHeaders[${index}].name`}
                            label={t`Header Name`}
                            inputProps={DEFAULT_INPUT_PROPS}
                            fullWidth
                        />
                        <RequestHeaderValueField
                            index={index}
                            fieldNamePrefix="testConfig.httpHeaders"
                            label={t`Header Value`}
                            initialName={header.name}
                        />
                        <IconButton
                            className={classes.actionButton}
                            onClick={
                                index === 0
                                    ? () => appendHttpHeaders({ ...EMPTY_HEADER })
                                    : () => removeHttpHeaders(index)
                            }
                        >
                            {index === 0 ? <AddIcon /> : <RemoveIcon />}
                        </IconButton>
                    </Box>
                ))}
            </TestSubFormSection>

            <TestAuthenticationSection authTypeRef={authTypeRef} />

            <NgpToggle
                fieldName="testConfig.disableServerCertVerification"
                label={t`Verify Server Certificate`}
                helpTipText={
                    <Trans>
                        Turning this feature OFF will allow the nPoint to ignore server certificate verification
                    </Trans>
                }
                inverted
            />
        </TestFormSection>
    );
};

export default TestRequestParametersSection;

TestRequestParametersSection.propTypes = {
    authTypeRef: PropTypes.shape({ current: PropTypes.string }),
};

TestRequestParametersSection.defaultProps = {
    authTypeRef: undefined,
};
